// ProductList.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { EyeIcon, EyeSlashIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import axios from '../axios';

import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
} from '@tanstack/react-table';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button } from '../shadcn/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../shadcn/ui/table';
import ProductForm from './ProductForm';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import CreateCategoryDialog from './CreateCategoryDialog';
import SkeletonLoader from './SkeletonLoader';
import MediaViewDialog from '../Components/Dialog/MediaViewDialog';
import { Checkbox } from '../shadcn/ui/checkbox';
import { toast } from '@/shadcn/ui/use-toast';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../shadcn/ui/dialog';
import config from '../config';
import { useCategoryTree } from '../hooks/useCategoryTree';


const ProductList = () => {
    const [products, setProducts] = useState([]);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [deleteProductId, setDeleteProductId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
    const [privateBlendCategoryIds, setPrivateBlendCategoryIds] = useState(new Set());
    const [isUpdatingPrivateBlendCategory, setIsUpdatingPrivateBlendCategory] = useState(false);
    const [isPrivateBlendModalOpen, setIsPrivateBlendModalOpen] = useState(false);
    const { categoryTree } = useCategoryTree();

    const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
    const [selectedMedia, setSelectedMedia] = useState(null);

    const handleViewMedia = (mediaItem) => {
        setSelectedMedia(mediaItem);
        setIsViewDialogOpen(true);
    };

    const handleCloseViewDialog = () => {
        setIsViewDialogOpen(false);
        setSelectedMedia(null);
    };

    useEffect(() => {
        fetchProducts();
    }, []);

    const categoryOptions = useMemo(() => {
        const map = new Map();
        products.forEach((product) => {
            const category = product.category;
            if (category?.id) {
                const id = String(category.id);
                if (!map.has(id)) {
                    map.set(id, {
                        id,
                        name: category.name || category.slug || `Κατηγορία ${category.id}`,
                    });
                }
            }
        });
        return Array.from(map.values());
    }, [products]);

    const siteBaseUrl = (config?.siteUrl || '').replace(/\/$/, '');

    const categoryMap = useMemo(() => {
        const map = {};
        const traverse = (nodes = []) => {
            nodes.forEach((node) => {
                if (!node) return;
                const id = String(node.id);
                map[id] = {
                    id,
                    slug: node.slug,
                    parent_id: node.parent_id === null || node.parent_id === undefined ? null : String(node.parent_id),
                };
                if (Array.isArray(node.children) && node.children.length) {
                    traverse(node.children);
                }
            });
        };
        traverse(categoryTree);
        return map;
    }, [categoryTree]);

    const buildCategorySlugPath = useCallback(
        (category) => {
            if (!category) return [];
            const slugPath = [];
            let currentId = category.id ? String(category.id) : category.category_id ? String(category.category_id) : null;
            if (!currentId && category.slug) {
                return [category.slug];
            }

            const safety = new Set();
            while (currentId && categoryMap[currentId] && !safety.has(currentId)) {
                safety.add(currentId);
                const node = categoryMap[currentId];
                if (node.slug) {
                    slugPath.unshift(node.slug);
                }
                currentId = node.parent_id;
            }

            if (!slugPath.length && category.slug) {
                slugPath.push(category.slug);
            }

            return slugPath;
        },
        [categoryMap]
    );

    const getProductUrl = useCallback(
        (product) => {
            if (!siteBaseUrl) return '#';
            const slugSegments = buildCategorySlugPath(product.category);
            const pathSegments = [...slugSegments, product?.slug].filter(Boolean);
            if (!pathSegments.length) return siteBaseUrl;
            return `${siteBaseUrl}/${pathSegments.join('/')}`;
        },
        [buildCategorySlugPath, siteBaseUrl]
    );

    const fetchProducts = useCallback(() => {
        setIsLoading(true);

        axios.get('/api/products', {
            withCredentials: true,
        })
            .then(response => {
                setProducts(response.data);
            })
            .catch(error => {
                console.error("There was an error fetching the products!", error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    useEffect(() => {
        const fetchPrivateBlendCategories = async () => {
            try {
                const response = await axios.get('/api/private-blend/categories');
                const ids =
                    response?.data?.category_ids ||
                    response?.data?.categories?.map((c) => c.id ?? c.category_id) ||
                    [];
                setPrivateBlendCategoryIds(new Set((ids || []).map((id) => String(id))));
            } catch (error) {
                console.error('Failed to load private blend categories', error);
            }
        };

        fetchPrivateBlendCategories();
    }, []);

    const handleOpenForm = useCallback((product) => {
        setEditingProduct(product);
        setIsFormOpen(true);
    }, []);

    const handleTogglePrivateBlendCategory = useCallback(
        async (categoryId, isChecked) => {
            if (!categoryId) {
                return;
            }
            const idStr = String(categoryId);
            const nextSelection = new Set(privateBlendCategoryIds);
            if (isChecked) {
                nextSelection.add(idStr);
            } else {
                nextSelection.delete(idStr);
            }
            setPrivateBlendCategoryIds(nextSelection);

            setIsUpdatingPrivateBlendCategory(true);
            try {
                const payload = {
                    category_ids: Array.from(nextSelection),
                };
                await axios.post('/api/private-blend/categories', payload);
                toast({
                    title: 'Επιτυχία',
                    description: 'Η κατηγορία ενημερώθηκε για το Private Blend.',
                    variant: 'success',
                });
            } catch (error) {
                console.error('Failed to update private blend categories', error);
                toast({
                    title: 'Σφάλμα',
                    description: 'Δεν ήταν δυνατή η ενημέρωση της κατηγορίας.',
                    variant: 'error',
                });
                setPrivateBlendCategoryIds((prev) => {
                    const rollback = new Set(prev);
                    if (isChecked) {
                        rollback.delete(idStr);
                    } else {
                        rollback.add(idStr);
                    }
                    return rollback;
                });
            } finally {
                setIsUpdatingPrivateBlendCategory(false);
            }
        },
        [privateBlendCategoryIds]
    );

    const handleCloseForm = useCallback(() => {
        setIsFormOpen(false);
        setEditingProduct(null);
    }, []);

    const handleDelete = useCallback((productId) => {
        setDeleteProductId(productId);
    }, []);

    const confirmDelete = useCallback(() => {
        axios.delete(`/api/products/${deleteProductId}`)
            .then(() => {
                setProducts(prev => prev.filter(p => p.product_id !== deleteProductId));
                setDeleteProductId(null);
            })
            .catch(error => console.error("Failed to delete product:", error));
    }, [deleteProductId]);

    const cancelDelete = useCallback(() => {
        setDeleteProductId(null);
    }, []);

    // const onSaveBasicInfo = async (formData) => {
    //     console.log(formData);

    //     for (let [key, value] of formData.entries()) {
    //         console.log(`${key}:`, value);
    //     }

    //     try {
    //         const config = {
    //             headers: {
    //                 'Content-Type': 'multipart/form-data',
    //             }
    //         };

    //         // Check if editingProduct is defined
    //         if (editingProduct) {
    //             formData.append('_method', 'PUT'); // Use PUT for updating
    //             const response = await axios.post(`/api/products/${editingProduct.product_id}`, formData, config);
    //             // Update the product in the state
    //             setProducts((prevProducts) => {
    //                 return prevProducts.map((product) =>
    //                     product.product_id === editingProduct.product_id ? { ...product, ...response.data } : product
    //                 );
    //             });
    //         } else {
    //             // Creating a new product
    //             const response = await axios.post('/api/products', formData, config); // Adjust endpoint for new product
    //             // Add the new product to the state
    //             console.log(response);
    //             setProducts((prevProducts) => [...prevProducts, response.data]);
    //         }

    //         handleCloseForm(); // Assuming you have a function to close the form after saving
    //     } catch (error) {
    //         console.error('Error saving basic info:', error);
    //         // Handle error, such as showing an error message
    //     }
    // };


    const onSaveBasicInfo = async (formData) => {
        console.log(formData);

        for (let [key, value] of formData.entries()) {
            console.log(`${key}:`, value);
        }

        try {
            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                }
            };

            let response;

            if (editingProduct) {
                formData.append('_method', 'PUT');
                response = await axios.post(`/api/products/${editingProduct.product_id}`, formData, config);
            } else {
                response = await axios.post('/api/products', formData, config);
            }

            if (response.status === 200 || response.status === 201) {
                const updatedProduct = response.data;

                setProducts((prevProducts) => {
                    if (editingProduct) {
                        return prevProducts.map((product) =>
                            product.product_id === editingProduct.product_id
                                ? { ...product, ...updatedProduct }
                                : product
                        );
                    } else {
                        return [...prevProducts, updatedProduct];
                    }
                });

                toast({
                    title: 'Επιτυχία',
                    description: editingProduct ? 'Το προϊόν ενημερώθηκε' : 'Το προϊόν δημιουργήθηκε',
                    variant: 'success',
                });

                handleCloseForm();
            } else {
                throw new Error('Αποτυχία αποθήκευσης προϊόντος');
            }
        } catch (error) {
            console.error('Error saving basic info:', error);
            toast({
                title: 'Σφάλμα',
                description: 'Η αποθήκευση απέτυχε. Παρακαλώ δοκιμάστε ξανά.',
                variant: 'error',
            });
        }
    };


    const onSaveSeoMeta = async (seoMetaData) => {
        console.log(seoMetaData);
        try {
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                },
            };

            let response;
            if (seoMetaData.seo_meta_id) {  // Check if there's an SEO meta ID
                // If SEO meta ID exists, update the SEO meta data
                response = await axios.put(`/api/seo-meta/${seoMetaData.seo_meta_id}`, seoMetaData, config);
            } else {
                // If no SEO meta ID, create new SEO meta data
                response = await axios.post('/api/seo-meta', seoMetaData, config);
            }

            // Check if response contains product_id
            if (!response.data.id) {
                // If response does not include product_id, you may need to get the product separately or include it in the response
                console.error('Response does not contain product_id');
                return;
            }

            // Update the product in the state with the new/updated SEO meta data
            setProducts((prevProducts) =>
                prevProducts.map((product) =>
                    product.product_id === seoMetaData.product_id ? { ...product, seo_meta: response.data } : product
                )
            );

            handleCloseForm(); // Close the form after saving
        } catch (error) {
            console.error('Error saving SEO meta:', error);
        }
    };


    const columns = [
        {
            accessorKey: 'image_url',
            header: 'Εικόνα',
            cell: info => {
                const media = info.row.original.media;
                const fullFilePath = media?.full_file_path;

                return fullFilePath ? (
                    <img
                        onClick={() => handleViewMedia(media)}
                        className="rounded-md cursor-pointer h-16 w-16 rounded-md object-cover"
                        src={fullFilePath}
                        alt={info.row.original.name || ''}
                    />
                ) : (
                    <span className="text-gray-400">Χωρίς εικόνα</span>
                );
            },
        },
        {
            accessorKey: 'name',
            header: 'Όνομα',
            cell: info => {
                const product = info.row.original;
                const url = getProductUrl(product);
                return url && url !== '#' ? (
                    <a
                        href={url}
                        className="text-blue-600 hover:underline"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {info.getValue()}
                    </a>
                ) : (
                    <span>{info.getValue()}</span>
                );
            },
        },
        {
            accessorKey: 'category',
            header: 'Κατηγορία',
            cell: info => {
                const category = info.row.original.category;
                return category ? (
                    <span>{category.name}</span>
                ) : (
                    <span className="text-gray-400">Χωρίς κατηγορία</span>
                );
            },
        },
        {
            accessorKey: 'visibility',
            header: 'Ορατότητα',
            cell: info => (
                <div className="relative flex items-center justify-center space-x-2 group">
                    {info.getValue() === 'public' ? (
                        <EyeIcon className="h-5 w-5 text-green-500" />
                    ) : (
                        <EyeSlashIcon className="h-5 w-5 text-red-500" />
                    )}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block">
                        <div className="px-2 py-1 bg-gray-800 text-white text-xs rounded shadow-lg">
                            {info.getValue() === 'public' ? 'Δημόσιο' : 'Προσχέδιο'}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            id: 'actions',
            header: 'Επεξεργασία/Διαγραφή',
            enableSorting: false, // Disable sorting for the actions column
            cell: info => (
                <div className="flex space-x-2 justify-self-end justify-end">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenForm(info.row.original)}
                    >
                        <PencilIcon className="h-5 w-5 text-blue-500" />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(info.row.original.product_id)}
                    >
                        <TrashIcon className="h-5 w-5 text-red-500" />
                    </Button>
                </div>
            ),
        },
    ];

    const table = useReactTable({
        data: products,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const handleCategoryCreated = (newCategory) => {
        setCategories((prevCategories) => [...prevCategories, newCategory]);
        setBasicInfo((prev) => ({ ...prev, category_id: newCategory.id })); // Ensure newCategory is defined
    };

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <h1 className="text-2xl font-bold">Εξοπλισμός</h1>
                <div className="flex items-center gap-2">
                    <Button width='fir-content' className="mr-2" onClick={() => setIsCategoryDialogOpen(true)}>
                        Κατηγορίες
                    </Button>
                    <Button onClick={() => handleOpenForm(null)}>Νέο Εξοπλισμό</Button>
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setIsPrivateBlendModalOpen(true)}
                        aria-label="Ρυθμίσεις Private Blend"
                    >
                        <EllipsisVerticalIcon className="h-5 w-5" />
                    </Button>
                </div>
            </div>

            <div className="mt-4 pt-4">
                {isLoading ? (
                    <div className="mt-6">
                        <SkeletonLoader />
                        <SkeletonLoader />
                        <SkeletonLoader />
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            {table.getHeaderGroups().map(headerGroup => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map(header => (
                                        <TableHead key={header.id} className="text-center">
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {table.getRowModel().rows.map((row, index) => (
                                <TableRow key={row.id} style={{ animationDelay: `${index * 0.1}s` }}>
                                    {row.getVisibleCells().map(cell => (
                                        <TableCell key={cell.id} className="text-center justify-items-center">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </div>
            <div className="flex justify-between items-center mt-4">
                <Button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
                    Προηγούμενο
                </Button>
                <span>Σελίδα {table.getState().pagination.pageIndex + 1} από {table.getPageCount()}</span>
                <Button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                    Επόμενο
                </Button>
            </div>

            <ProductForm
                isOpenProductForm={isFormOpen}
                onCloseProductForm={handleCloseForm}
                onSaveBasicInfo={onSaveBasicInfo}
                onSaveSeoMeta={onSaveSeoMeta}
                initialValues={editingProduct}
                parentId="2"
            />

            {/* <DeleteConfirmationDialog
                isOpen={!!deleteProductId}
                onClose={cancelDelete}
                onDelete={confirmDelete}
                productName={products.find(p => p.id === deleteProductId)?.name}
            /> */}
            <DeleteConfirmationDialog
                isOpen={!!deleteProductId}
                onClose={cancelDelete}
                onDelete={confirmDelete}
                confirmationTitle={'Διαγραφή εξοπλισμό'}
                confirmationMessage={`Είστε βέβαιοι ότι θέλετε να διαγράψετε τον εξοπλισμό "${products.find(p => p.product_id === deleteProductId)?.name}";`}
            />

            <MediaViewDialog
                isOpen={isViewDialogOpen}
                onClose={handleCloseViewDialog}
                mediaItem={selectedMedia}
            />

            <CreateCategoryDialog
                isOpenCategoryDialog={isCategoryDialogOpen}
                onCloseCategoryDialog={() => setIsCategoryDialogOpen(false)}
                onCategoryCreated={handleCategoryCreated}
                parentId="2"
            />

            <Dialog open={isPrivateBlendModalOpen} onOpenChange={setIsPrivateBlendModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Private Blend</DialogTitle>
                        <DialogDescription>
                            Επιλέξτε ποιες κατηγορίες θα συμπεριληφθούν στο Private Blend.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        {categoryOptions.length ? (
                            categoryOptions.map((category) => (
                                <label
                                    key={category.id}
                                    className="flex items-center gap-3 rounded-md border px-3 py-2"
                                >
                                    <Checkbox
                                        checked={privateBlendCategoryIds.has(category.id)}
                                        disabled={isUpdatingPrivateBlendCategory}
                                        onCheckedChange={(value) =>
                                            handleTogglePrivateBlendCategory(category.id, Boolean(value))
                                        }
                                    />
                                    <span className="text-sm text-gray-800">{category.name}</span>
                                </label>
                            ))
                        ) : (
                            <p className="text-sm text-gray-500">Δεν υπάρχουν κατηγορίες προϊόντων.</p>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ProductList;
